/**
 * WORKFLOW MODEL — kieu du lieu + ham thuan tuy dung chung Gateway & Admin SPA.
 *
 * Workflow = chuoi node chay TUAN TU: Start -> ... -> End.
 * Node co the la ProviderModel, ModelGroup, hoac node chuc nang (text/image/video).
 * Moi node model/group co 2 cong ra: 'success' va 'failure' -> re nhanh theo ket qua.
 *
 * Voi phan mem thu 3, workflow lo ra y het 1 nhom model (catalog + /jobs + webhook).
 */

import type { ParamField, ParamType } from './modelParams';
import { UPLOAD_TYPES } from './modelParams';

// ---------------------------------------------------------------------------
// Graph
// ---------------------------------------------------------------------------

export type WorkflowNodeType =
  | 'start'
  | 'end'
  | 'model' // 1 ProviderModel cu the
  | 'group' // 1 ModelGroup (co failover/race rieng cua nhom)
  | 'text' // node chuc nang: sinh chuoi (hang so hoac template)
  | 'image' // node chuc nang: URL anh (hang so hoac template)
  | 'video' // node chuc nang: URL video (hang so hoac template)
  | 'condition'; // node DIEU KIEN: re nhanh theo gia tri (moi truong hop = 1 cong ra)

/** Loai cong (port) — quyet dinh mau + kiem tra tuong thich khi noi day. */
export type WorkflowPortKind = 'text' | 'image' | 'video';

/**
 * Cong ra: node chuc nang/start dung 'out'; node model/group dung success|failure;
 * node dieu kien dung "case:<id>" cho tung truong hop + 'else' cho "con lai".
 */
export type WorkflowPort = 'out' | 'success' | 'failure' | 'else' | `case:${string}`;

/** Toan tu so sanh cua 1 truong hop trong node dieu kien. */
export type WorkflowConditionOperator =
  | 'equals' // bang
  | 'not_equals' // khac
  | 'contains' // chua
  | 'not_contains' // khong chua
  | 'starts_with' // bat dau bang
  | 'regex' // khop bieu thuc chinh quy
  | 'gt' // lon hon (so)
  | 'lt' // nho hon (so)
  | 'empty' // rong
  | 'not_empty'; // khong rong

/** 1 truong hop (nhanh) cua node dieu kien — moi case la 1 cong ra "case:<id>". */
export interface WorkflowConditionCase {
  /** Id on dinh (editor sinh, vd "c1") — dung lam cong ra, KHONG doi khi sua value. */
  id: string;
  operator: WorkflowConditionOperator;
  /** Gia tri so sanh (bo qua voi empty/not_empty). */
  value?: string;
}

export interface WorkflowNode {
  /** Id on dinh trong graph (do editor sinh, vd "n1"). */
  id: string;
  type: WorkflowNodeType;
  /** Ten hien thi tren canvas. */
  name?: string;
  position: { x: number; y: number };
  /** node 'model' -> ProviderModel.id; node 'group' -> ModelGroup.id. */
  ref?: string;
  /**
   * node model/group: gia tri tham so dau vao (ten CANONICAL).
   * Chuoi co the chua placeholder {{input.x}} / {{nodes.n1.output_url}}.
   */
  params?: Record<string, any>;
  /** node chuc nang text/image/video: hang so hoac template. */
  template?: string;
  /** node dieu kien: gia tri dem so sanh (template, vd "{{input.aspect_ratio}}"). */
  condInput?: string;
  /** node dieu kien: cac truong hop, xet TU TREN XUONG — khop dau tien thang. */
  cases?: WorkflowConditionCase[];
}

export interface WorkflowEdge {
  id: string;
  /** node id nguon. */
  from: string;
  fromPort: WorkflowPort;
  /** node id dich. */
  to: string;
}

export interface WorkflowGraph {
  version: 1;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** Ket qua chuan hoa cua 1 node (giong normalizeJobOutput). */
export interface WorkflowNodeOutput {
  output_text?: string;
  output_url?: string;
  output_urls?: string[];
  /** Job con da tao ra ket qua nay (node model/group) — de End lay output THO day du. */
  jobId?: string;
  /**
   * INPUT DA RESOLVE cua buoc nay (node model/group) — luu lai de node phia sau tham
   * chieu nguoc, vd node Dieu kien so sanh {{nodes.<id>.input.aspect_ratio}} (gia tri
   * THAT da gui cho buoc do, sau khi interpolate).
   */
  input?: Record<string, any>;
}

/** Trang thai chay — luu o Job.workflowState (job cha) de khoi phuc sau restart. */
export interface WorkflowRunState {
  /** So lan da chay lai TOAN BO workflow (Workflow.retryCount). */
  attempt: number;
  /** So buoc da chay trong luot nay — chan vong lap vo han qua canh 'failure'. */
  stepCount: number;
  /** Node dang chay (node model/group dang co job con). */
  currentNodeId: string | null;
  /** Job con dang chay. */
  currentChildJobId: string | null;
  /** Output cua tung node da chay xong. */
  nodeOutputs: Record<string, WorkflowNodeOutput>;
  /** Node vua cap du lieu (de End biet lay output nao). */
  lastNodeId?: string | null;
  warnings?: string[];
}

export const WORKFLOW_MAX_NODES = 30;
export const WORKFLOW_MAX_STEPS_DEFAULT = 20;

/**
 * So do khoi tao: Start -> End da noi san (HOP LE ngay) de admin tao moi khong bi
 * chan boi validate. Admin chen cac node vao giua roi noi lai day.
 */
export function emptyWorkflowGraph(): WorkflowGraph {
  return {
    version: 1,
    nodes: [
      { id: 'start', type: 'start', name: 'Bắt đầu', position: { x: 80, y: 200 } },
      { id: 'end', type: 'end', name: 'Kết thúc', position: { x: 720, y: 200 } },
    ],
    edges: [{ id: 'e-start-end', from: 'start', fromPort: 'out', to: 'end' }],
  };
}

export function newWorkflowRunState(): WorkflowRunState {
  return { attempt: 0, stepCount: 0, currentNodeId: null, currentChildJobId: null, nodeOutputs: {} };
}

// ---------------------------------------------------------------------------
// Port kind
// ---------------------------------------------------------------------------

/** Loai cong cua 1 tham so (dua theo ParamType). */
export function portKindOfParamType(t: ParamType): WorkflowPortKind {
  if (t === 'video' || t === 'video[]') return 'video';
  if (t === 'image' || t === 'image[]' || t === 'media' || t === 'audio') return 'image';
  return 'text';
}

/** Cac cong dau vao cua node model/group, suy tu paramsSchema cua model/nhom do. */
export function inputPortsOfSchema(schema?: ParamField[] | null): Array<{ name: string; label: string; kind: WorkflowPortKind; type: ParamType }> {
  if (!Array.isArray(schema)) return [];
  return schema
    .filter((f) => f.enabled !== false)
    .map((f) => ({ name: f.name, label: f.label || f.name, kind: portKindOfParamType(f.type), type: f.type }));
}

/** Cong dau vao NHAN media (anh/video) — dung de kiem tra tuong thich khi noi day. */
export function isMediaParam(f: ParamField): boolean {
  return UPLOAD_TYPES.includes(f.type);
}

/** Loai output cua node chuc nang. */
export function portKindOfNodeType(t: WorkflowNodeType): WorkflowPortKind | null {
  if (t === 'text') return 'text';
  if (t === 'image') return 'image';
  if (t === 'video') return 'video';
  return null;
}

// ---------------------------------------------------------------------------
// Template interpolation: {{input.<field>}} | {{nodes.<nodeId>.<field>}}
// ---------------------------------------------------------------------------

export interface WorkflowCtx {
  input: Record<string, any>;
  nodes: Record<string, WorkflowNodeOutput>;
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_.\[\]-]+)\s*\}\}/g;

/** Doc 1 duong dan "input.prompt" | "nodes.n1.output_url" tu ctx. */
export function readPath(path: string, ctx: WorkflowCtx): any {
  const parts = path.split('.');
  let cur: any = parts[0] === 'input' ? ctx.input : parts[0] === 'nodes' ? ctx.nodes : undefined;
  if (cur === undefined) return undefined;
  for (const p of parts.slice(1)) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

/**
 * Thay placeholder trong 1 chuoi.
 * - Chuoi CHi la 1 placeholder -> tra NGUYEN gia tri (giu kieu: mang, so, ...).
 * - Placeholder nhung giua chu -> noi chuoi.
 */
export function interpolate(tpl: string, ctx: WorkflowCtx): any {
  if (typeof tpl !== 'string' || !tpl.includes('{{')) return tpl;
  const whole = tpl.trim().match(/^\{\{\s*([a-zA-Z0-9_.\[\]-]+)\s*\}\}$/);
  if (whole) {
    const v = readPath(whole[1], ctx);
    return v === undefined ? '' : v;
  }
  return tpl.replace(PLACEHOLDER_RE, (_m, path) => {
    const v = readPath(path, ctx);
    if (v === undefined || v === null) return '';
    return typeof v === 'string' ? v : JSON.stringify(v);
  });
}

/** Ap dung interpolate cho toan bo params (de quy qua object/array). */
export function resolveParams(params: any, ctx: WorkflowCtx): any {
  if (typeof params === 'string') return interpolate(params, ctx);
  if (Array.isArray(params)) return params.map((v) => resolveParams(v, ctx));
  if (params && typeof params === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(params)) {
      const r = resolveParams(v, ctx);
      if (r === '' || r === undefined || r === null) continue; // bo field rong -> khong gui
      out[k] = r;
    }
    return out;
  }
  return params;
}

/** Liet ke moi placeholder xuat hien trong graph (de validate). */
export function collectPlaceholders(value: any, acc: string[] = []): string[] {
  if (typeof value === 'string') {
    let m: RegExpExecArray | null;
    const re = new RegExp(PLACEHOLDER_RE.source, 'g');
    while ((m = re.exec(value))) acc.push(m[1]);
  } else if (Array.isArray(value)) {
    value.forEach((v) => collectPlaceholders(v, acc));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((v) => collectPlaceholders(v, acc));
  }
  return acc;
}

// ---------------------------------------------------------------------------
// Duyet graph
// ---------------------------------------------------------------------------

export function findNode(graph: WorkflowGraph, id: string): WorkflowNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

export function startNode(graph: WorkflowGraph): WorkflowNode | undefined {
  return graph.nodes.find((n) => n.type === 'start');
}

/** Node ke tiep theo 1 cong ra. */
export function nextNode(graph: WorkflowGraph, fromId: string, port: WorkflowPort): WorkflowNode | undefined {
  const edge = graph.edges.find((e) => e.from === fromId && e.fromPort === port);
  return edge ? findNode(graph, edge.to) : undefined;
}

/** Node model/group hay khong (tao job con). */
export function isJobNode(n: WorkflowNode): boolean {
  return n.type === 'model' || n.type === 'group';
}

/**
 * Node CHUC NANG (Van ban / Anh / Video). Day KHONG phai buoc chay that — no chi
 * sinh ra mot gia tri (hang so hoac template). No co the nam tren luong tuan tu,
 * HOAC la nhanh ben chi de CAP DU LIEU cho mot node model (giong n8n) — nen node
 * chuc nang KHONG bat buoc phai co duong vao.
 */
export function isFunctionNode(n: WorkflowNode): boolean {
  return n.type === 'text' || n.type === 'image' || n.type === 'video';
}

/** Node DIEU KIEN — nam TREN luong chay, re nhanh theo gia tri (khong tao job con). */
export function isConditionNode(n: WorkflowNode): boolean {
  return n.type === 'condition';
}

/** Nhan tieng Viet cua toan tu (dung chung editor + log). */
export const CONDITION_OPERATOR_LABEL: Record<WorkflowConditionOperator, string> = {
  equals: 'bằng',
  not_equals: 'khác',
  contains: 'chứa',
  not_contains: 'không chứa',
  starts_with: 'bắt đầu bằng',
  regex: 'khớp regex',
  gt: 'lớn hơn (số)',
  lt: 'nhỏ hơn (số)',
  empty: 'rỗng',
  not_empty: 'không rỗng',
};

/** Toan tu KHONG can nhap gia tri so sanh. */
export function operatorNeedsValue(op: WorkflowConditionOperator): boolean {
  return op !== 'empty' && op !== 'not_empty';
}

/** So sanh 1 truong hop. `actual` da duoc interpolate thanh chuoi. */
export function evalConditionCase(actual: string, c: WorkflowConditionCase): boolean {
  const expected = String(c.value ?? '').trim();
  const a = actual.trim();
  switch (c.operator) {
    case 'equals':
      return a === expected;
    case 'not_equals':
      return a !== expected;
    case 'contains':
      return expected !== '' && a.includes(expected);
    case 'not_contains':
      return !a.includes(expected);
    case 'starts_with':
      return expected !== '' && a.startsWith(expected);
    case 'regex':
      try {
        return new RegExp(expected).test(a);
      } catch {
        return false; // regex sai cu phap -> coi nhu khong khop (validate da canh bao)
      }
    case 'gt': {
      const x = parseFloat(a);
      const y = parseFloat(expected);
      return Number.isFinite(x) && Number.isFinite(y) && x > y;
    }
    case 'lt': {
      const x = parseFloat(a);
      const y = parseFloat(expected);
      return Number.isFinite(x) && Number.isFinite(y) && x < y;
    }
    case 'empty':
      return a === '';
    case 'not_empty':
      return a !== '';
    default:
      return false;
  }
}

/** Tim truong hop KHOP DAU TIEN (xet tu tren xuong). Null = khong khop -> nhanh 'else'. */
export function matchConditionCase(node: WorkflowNode, actual: string): WorkflowConditionCase | null {
  for (const c of node.cases ?? []) {
    if (evalConditionCase(actual, c)) return c;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validate — dung chung cho Admin (truoc khi luu) va Gateway (khi luu + khi chay)
// ---------------------------------------------------------------------------

/** Tra ve danh sach loi (tieng Viet). Rong = hop le. */
export function validateWorkflowGraph(graph: WorkflowGraph | null | undefined): string[] {
  const errs: string[] = [];
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) {
    return ['Sơ đồ workflow không hợp lệ (thiếu nodes/edges).'];
  }
  const nodes = graph.nodes;
  if (nodes.length > WORKFLOW_MAX_NODES) errs.push(`Quá ${WORKFLOW_MAX_NODES} node (hiện ${nodes.length}).`);

  const ids = new Set<string>();
  for (const n of nodes) {
    if (!n.id) errs.push('Có node thiếu id.');
    else if (ids.has(n.id)) errs.push(`Trùng id node: ${n.id}.`);
    ids.add(n.id);
    if (isJobNode(n) && !n.ref) errs.push(`Node "${n.name || n.id}" chưa chọn ${n.type === 'model' ? 'model' : 'nhóm model'}.`);
  }

  const starts = nodes.filter((n) => n.type === 'start');
  const ends = nodes.filter((n) => n.type === 'end');
  if (starts.length !== 1) errs.push(`Phải có đúng 1 node Bắt đầu (hiện ${starts.length}).`);
  if (ends.length < 1) errs.push('Phải có ít nhất 1 node Kết thúc.');

  for (const e of graph.edges) {
    if (!ids.has(e.from)) errs.push(`Đường nối trỏ từ node không tồn tại: ${e.from}.`);
    if (!ids.has(e.to)) errs.push(`Đường nối trỏ tới node không tồn tại: ${e.to}.`);
  }

  // LUONG CHAY: BFS tu Start theo cong 'ra'/'thanh cong'/cac nhanh dieu kien. Cac node
  // tren luong nay la "buoc chay"; node chuc nang nam NGOAI chi la nguon du lieu (nhanh ben).
  // Node dieu kien re nhieu nhanh -> phai duyet MOI nhanh, chi can 1 nhanh toi duoc End.
  const start = starts[0];
  const onPath = new Set<string>();
  let reachedEnd = false;
  if (start) {
    const queue: WorkflowNode[] = [start];
    while (queue.length) {
      const cur = queue.shift()!;
      if (onPath.has(cur.id)) continue;
      onPath.add(cur.id);
      if (cur.type === 'end') { reachedEnd = true; continue; }
      const ports: WorkflowPort[] = isJobNode(cur)
        ? ['success']
        : isConditionNode(cur)
          ? [...(cur.cases ?? []).map((c): WorkflowPort => `case:${c.id}`), 'else']
          : ['out'];
      for (const p of ports) {
        const nx = nextNode(graph, cur.id, p);
        if (nx) queue.push(nx);
      }
    }
    if (!reachedEnd) errs.push('Đường "thành công" từ node Bắt đầu không tới được node Kết thúc.');
  }

  for (const n of nodes) {
    if (n.type === 'end') continue;
    const outs = graph.edges.filter((e) => e.from === n.id);
    if (isJobNode(n)) {
      const succ = outs.filter((e) => e.fromPort === 'success');
      const fail = outs.filter((e) => e.fromPort === 'failure');
      if (succ.length !== 1) errs.push(`Node "${n.name || n.id}" phải có đúng 1 đường "thành công" (hiện ${succ.length}).`);
      if (fail.length > 1) errs.push(`Node "${n.name || n.id}" chỉ được 1 đường "thất bại".`);
    } else if (isConditionNode(n)) {
      const label = n.name || n.id;
      if (!String(n.condInput ?? '').trim()) errs.push(`Node điều kiện "${label}" chưa nhập giá trị đem so sánh.`);
      const cases = n.cases ?? [];
      if (!cases.length) errs.push(`Node điều kiện "${label}" chưa có trường hợp nào.`);
      const caseIds = new Set(cases.map((c) => c.id));
      for (const c of cases) {
        if (operatorNeedsValue(c.operator) && !String(c.value ?? '').trim()) {
          errs.push(`Node điều kiện "${label}": trường hợp "${CONDITION_OPERATOR_LABEL[c.operator] || c.operator}" chưa nhập giá trị.`);
        }
        if (c.operator === 'regex' && c.value) {
          try { new RegExp(c.value); } catch { errs.push(`Node điều kiện "${label}": regex "${c.value}" sai cú pháp.`); }
        }
        const n_ = outs.filter((e) => e.fromPort === `case:${c.id}`).length;
        if (n_ !== 1) errs.push(`Node điều kiện "${label}": trường hợp "${c.value ?? c.id}" phải có đúng 1 đường ra (hiện ${n_}).`);
      }
      for (const e of outs) {
        if (e.fromPort.startsWith('case:') && !caseIds.has(e.fromPort.slice(5))) {
          errs.push(`Node điều kiện "${label}" có đường nối tới trường hợp không còn tồn tại.`);
        }
      }
      if (outs.filter((e) => e.fromPort === 'else').length > 1) {
        errs.push(`Node điều kiện "${label}" chỉ được 1 đường "còn lại".`);
      }
    } else {
      const o = outs.filter((e) => e.fromPort === 'out');
      // Node chuc nang lam NGUON DU LIEU duoc phep noi toi NHIEU node (vd 1 node Ảnh dùng
      // cho 2 model). Chi khi no nam TREN luong tuan tu moi bat buoc dung 1 duong ra —
      // nhieu hon thi khong biet chay tiep nhanh nao.
      if (o.length < 1) errs.push(`Node "${n.name || n.id}" chưa có đường ra.`);
      else if (o.length > 1 && (n.type === 'start' || onPath.has(n.id))) {
        errs.push(`Node "${n.name || n.id}" nằm trên luồng chạy nên chỉ được 1 đường ra (hiện ${o.length}).`);
      }
    }
  }

  for (const n of nodes) {
    if (n.type === 'start') continue;
    // Node chuc nang duoc phep khong co duong vao: no la nhanh ben cap du lieu cho node
    // model (vd node Ảnh giữ URL cố định). Chi can no co duong RA (da kiem o tren).
    if (isFunctionNode(n)) continue;
    const incoming = graph.edges.some((e) => e.to === n.id);
    if (!incoming) errs.push(`Node "${n.name || n.id}" không có đường vào (mồ côi).`);
  }

  // Placeholder tro toi node co that.
  for (const n of nodes) {
    for (const p of collectPlaceholders([n.params, n.template, n.condInput])) {
      const parts = p.split('.');
      if (parts[0] === 'nodes' && !ids.has(parts[1])) {
        errs.push(
          `Node "${n.name || n.id}" tham chiếu node đã bị xóa: {{${p}}} — mở node này sửa lại tham số (vd trỏ về input công khai bằng nút "Lấy tham số từ input công khai").`,
        );
      }
    }
  }

  return Array.from(new Set(errs));
}

/**
 * Ap DEFAULT tu paramsSchema cua workflow vao input phan mem thu 3 gui len —
 * dong bo hanh vi voi nhom model rieng le (bodyHint dien san default). Chi dien
 * field thieu/rong; khong ghi de gia tri nguoi dung da gui.
 */
export function applyWorkflowInputDefaults(input: Record<string, any>, schema?: ParamField[] | null): {
  input: Record<string, any>;
  changed: boolean;
} {
  if (!Array.isArray(schema) || !schema.length) return { input, changed: false };
  let changed = false;
  const out = { ...(input || {}) };
  for (const f of schema) {
    if (f.enabled === false || f.default === undefined) continue;
    const cur = out[f.name];
    if (cur === undefined || cur === null || cur === '') {
      out[f.name] = f.default;
      changed = true;
    }
  }
  return { input: changed ? out : input, changed };
}

/** Node cap du lieu cho End dau tien (de suy outputType khi luu). */
export function nodeFeedingEnd(graph: WorkflowGraph): WorkflowNode | undefined {
  if (!Array.isArray(graph?.nodes) || !Array.isArray(graph?.edges)) return undefined;
  const end = graph.nodes.find((n) => n.type === 'end');
  if (!end) return undefined;
  const edge = graph.edges.find((e) => e.to === end.id && e.fromPort !== 'failure') ?? graph.edges.find((e) => e.to === end.id);
  return edge ? findNode(graph, edge.from) : undefined;
}
